import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthGuardData, createAuthGuard } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { StorageService } from '../services/storage.service';
import { PermissionService } from '../services/permission.service';
import { AuthService } from '../services/login.service';

// ─────────────────────────────────────────────────────────────────────────────
// Guard principal : Keycloak + vérification rôle d'accès à l'application
// ─────────────────────────────────────────────────────────────────────────────

const isAccessAllowed = async (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
  authData: AuthGuardData,
): Promise<boolean | UrlTree> => {

  const { authenticated } = authData;
  const keycloak     = inject(Keycloak);
  const router       = inject(Router);
  const storage      = inject(StorageService);
  const permissions  = inject(PermissionService);
  const authService  = inject(AuthService);

  if (!authenticated) {
    keycloak.login();
    return false;
  }

  // Stocker les tokens
  if (keycloak.token) {
    storage.setItem('access_token', keycloak.token);
  }
  if (keycloak.refreshToken) {
    storage.setItem('refresh_token', keycloak.refreshToken);
  }

  // Extraire userId
  if (keycloak.token) {
    try {
      const userId = authService.extractUserIdFromToken(keycloak.token);
      if (userId) storage.setItem('userId', userId);
    } catch { /* non bloquant */ }

    // ← Initialiser le PermissionService avec le token frais
    permissions.initFromToken(keycloak.token);
  }

  // Vérifier l'accès global à l'application
  if (!permissions.snapshot.canAccessApp) {
    // console.warn('[Guard] Accès refusé - rôle insuffisant');
    return router.createUrlTree(['/.']);
  }

  // Vérifier les permissions spécifiques à la route (data.requiredPermission)
  const requiredPermission: string | undefined = route.data?.['requiredPermission'];
  if (requiredPermission && !permissions.can(requiredPermission)) {
    // console.warn(`[Guard] Permission manquante : ${requiredPermission}`);
    return router.createUrlTree(['/.']);
  }

  // Vérifier les rôles spécifiques à la route (data.requiredRoles)
  const requiredRoles: string[] | undefined = route.data?.['requiredRoles'];
  if (requiredRoles?.length && !permissions.hasAnyRole(...requiredRoles as any)) {
    // console.warn(`[Guard] Rôles manquants : ${requiredRoles.join(', ')}`);
    return router.createUrlTree(['/.']);
  }

  return true;
};

/** Guard Keycloak à utiliser sur toutes les routes protégées */
export const keycloakGuard: CanActivateFn = createAuthGuard<CanActivateFn>(isAccessAllowed);

// ─────────────────────────────────────────────────────────────────────────────
// Guard léger pour les routes avec vérification de permission uniquement
// (sans re-vérification Keycloak, utilisable sur canActivateChild)
// ─────────────────────────────────────────────────────────────────────────────

export const permissionGuard = (action: string): CanActivateFn => {
  return (): boolean | UrlTree => {
    const permissions = inject(PermissionService);
    const router      = inject(Router);

    if (permissions.can(action)) return true;
    return router.createUrlTree(['/.']);
  };
};